import { z } from 'zod';

/*
 * How courses and quizzes are described for discovery: up to three topics
 * from a curated list (so catalog filters stay short and consistent), a
 * level, and free-form tags that only feed search.
 */

export const TOPICS = [
  { id: 'security-basics', label: 'Security basics' },
  { id: 'scams-fraud', label: 'Scams and fraud' },
  { id: 'phishing', label: 'Phishing and social engineering' },
  { id: 'accounts-identity', label: 'Passwords and identity' },
  { id: 'privacy', label: 'Privacy' },
  { id: 'devices-networks', label: 'Devices, networks and IoT' },
  { id: 'app-security', label: 'Application security' },
  { id: 'security-operations', label: 'Security operations' },
  { id: 'ai-security', label: 'AI and security' },
  { id: 'building-with-ai', label: 'Building with AI' },
] as const;
export type Topic = (typeof TOPICS)[number]['id'];
const TOPIC_IDS = TOPICS.map((t) => t.id) as [Topic, ...Topic[]];
export const topicSchema = z.enum(TOPIC_IDS);
/** Up to three topics per course or quiz. */
export const topicsSchema = z.array(topicSchema).max(3).default([]);
export const TOPIC_LABELS = Object.fromEntries(TOPICS.map((t) => [t.id, t.label])) as Record<
  Topic,
  string
>;

export const LEVELS = ['beginner', 'intermediate', 'advanced'] as const;
export type Level = (typeof LEVELS)[number];
export const levelSchema = z.enum(LEVELS);
export const LEVEL_LABELS: Record<Level, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};

/** Free-form search tags. */
export const tagsSchema = z.array(z.string().trim().min(1).max(40)).max(20).default([]);

/**
 * Topics suggested from question skills, so authors (and existing content)
 * start with sensible topics. Skills not listed suggest nothing.
 */
const SKILL_TOPICS: Record<string, Topic> = {
  phishing: 'phishing',
  social_engineering: 'phishing',
  safe_browsing: 'phishing',
  passwords: 'accounts-identity',
  account_security: 'accounts-identity',
  privacy: 'privacy',
  device_safety: 'devices-networks',
  iot_security: 'devices-networks',
  impersonation_scams: 'scams-fraud',
  payment_scams: 'scams-fraud',
  job_and_investment_scams: 'scams-fraud',
  romance_and_sextortion: 'scams-fraud',
  scam_recovery: 'scams-fraud',
  security_foundations: 'security-basics',
  threat_landscape: 'security-basics',
  attack_surfaces: 'app-security',
  application_security: 'app-security',
  security_operations: 'security-operations',
  ai_and_security: 'ai-security',
  agent_fundamentals: 'building-with-ai',
  tools_and_mcp: 'building-with-ai',
  multi_agent_systems: 'building-with-ai',
  memory_and_context: 'building-with-ai',
  evals_and_safety: 'building-with-ai',
  deployment_on_aws: 'building-with-ai',
};

/** The (up to three) topics most of these skills point to, most common first. */
export function suggestTopics(skills: readonly string[]): Topic[] {
  const counts = new Map<Topic, number>();
  for (const s of skills) {
    const t = SKILL_TOPICS[s];
    if (t) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([t]) => t);
}
